import React, { useState, useRef, useEffect } from "react";
import { Popover, ActionIcon, ScrollArea, Text, Group, Stack } from "@mantine/core";
import { TimeInput } from "@mantine/dates";
import { IconClock } from "@tabler/icons-react";

const PRIMARY_COLOR = "var(--vz-header-bg, #0d3b6e)";
const HOUR_OPTIONS = Array.from({ length: 24 }, (_, i) => i);
const MINUTE_OPTIONS = Array.from({ length: 60 }, (_, i) => i);

const getOptionStyle = (isSelected, isHovered) => ({
	padding: "6px 0",
	cursor: "pointer",
	textAlign: "center",
	borderRadius: 4,
	transition: "background-color 0.15s, color 0.15s",
	...(isSelected || isHovered
		? { backgroundColor: PRIMARY_COLOR, color: "white", fontWeight: isSelected ? 600 : 400 }
		: {}),
});

const columnHeaderStyle = {
	textAlign: "center",
	borderBottom: "1px solid #e9ecef",
	backgroundColor: "#f8f9fa",
};

const CustomTimeInput = ({ label, placeholder, required, value, onChange, onBlur, error, disabled }) => {
	const [opened, setOpened] = useState(false);
	const [hoveredHour, setHoveredHour] = useState(null);
	const [hoveredMinute, setHoveredMinute] = useState(null);
	const timeInputRef = useRef(null);
	const hourScrollRef = useRef(null);
	const minuteScrollRef = useRef(null);

	const now = new Date();
	const [hours = now.getHours(), minutes = now.getMinutes()] = String(value || "").split(":");
	const currentHour = parseInt(hours, 10) || now.getHours();
	const currentMinute = parseInt(minutes, 10) || now.getMinutes();

	useEffect(() => {
		if (!opened) return;

		const scrollToSelected = (ref, dataAttr, val) => {
			const element = ref.current?.querySelector(`[${dataAttr}="${val}"]`);
			if (element && ref.current) {
				const scrollPosition = element.offsetTop - ref.current.clientHeight / 2 + element.clientHeight / 2;
				ref.current.scrollTop = scrollPosition;
			}
		};

		setTimeout(() => {
			scrollToSelected(hourScrollRef, "data-hour", currentHour);
			scrollToSelected(minuteScrollRef, "data-minute", currentMinute);
		}, 100);
	}, [opened, currentHour, currentMinute]);

	const handleTimeSelect = (hour, minute) => {
		const newTime = `${hour.toString().padStart(2, "0")}:${minute.toString().padStart(2, "0")}`;

		onChange?.({
			target: { value: newTime, name: timeInputRef.current?.name || "" },
			currentTarget: { value: newTime, name: timeInputRef.current?.name || "" },
		});

		setOpened(false);
	};

	return (
		<Popover opened={opened} onChange={setOpened} position="bottom-start" shadow="md" disabled={disabled}>
			<Popover.Target>
				<TimeInput
					ref={timeInputRef}
					label={label}
					placeholder={placeholder}
					required={required}
					value={value}
					onChange={onChange}
					onBlur={onBlur}
					error={error || undefined}
					disabled={disabled}
					rightSection={
						<ActionIcon onClick={() => !disabled && setOpened((o) => !o)} disabled={disabled} variant="subtle">
							<IconClock size={14} />
						</ActionIcon>
					}
				/>
			</Popover.Target>

			<Popover.Dropdown p={0}>
				<Group spacing={0} align="flex-start">
					<Stack spacing="xs" style={{ borderRight: "1px solid #e9ecef", width: 80 }}>
						<Text size="xs" weight={600} p="xs" style={columnHeaderStyle}>
							Horas
						</Text>
						<ScrollArea h={240} type="never" viewportRef={hourScrollRef}>
							<Stack spacing={0}>
								{HOUR_OPTIONS.map((hour) => (
									<div
										key={hour}
										data-hour={hour}
										onClick={() => handleTimeSelect(hour, currentMinute)}
										onMouseEnter={() => setHoveredHour(hour)}
										onMouseLeave={() => setHoveredHour(null)}
										style={getOptionStyle(currentHour === hour, hoveredHour === hour)}
									>
										<Text size="xs">{hour.toString().padStart(2, "0")}</Text>
									</div>
								))}
							</Stack>
						</ScrollArea>
					</Stack>

					<Stack spacing={0} style={{ width: 80 }}>
						<Text size="xs" weight={600} p="xs" style={columnHeaderStyle}>
							Minutos
						</Text>
						<ScrollArea h={240} type="never" viewportRef={minuteScrollRef}>
							<Stack spacing={0}>
								{MINUTE_OPTIONS.map((minute) => (
									<div
										key={minute}
										data-minute={minute}
										onClick={() => handleTimeSelect(currentHour, minute)}
										onMouseEnter={() => setHoveredMinute(minute)}
										onMouseLeave={() => setHoveredMinute(null)}
										style={getOptionStyle(currentMinute === minute, hoveredMinute === minute)}
									>
										<Text size="xs">{minute.toString().padStart(2, "0")}</Text>
									</div>
								))}
							</Stack>
						</ScrollArea>
					</Stack>
				</Group>
			</Popover.Dropdown>
		</Popover>
	);
};

export default CustomTimeInput;
